import {
  type createScenarioRequestSchema,
  paginatedResultItemRefSchema,
  paginatedResultRiskItemEntrySchema,
  paginatedResultTeamRefSchema,
  scenarioActivationResponseSchema,
  scenarioAlertResponseSchema,
  scenarioChangeSummarySchema,
  scenarioImpactSummaryResponseSchema,
  scenarioOperationAcceptedResponseSchema,
  scenarioOperationResponseSchema,
  type scenarioUpdateRequestSchema,
} from '@tempo-io/adaptive-planner-dto';
import { z } from 'zod';
import { tempoApi } from '@/common/api/tempoApi';
import {
  parsedScenarioResponseSchema,
  parsedScenarioSchema,
  type ScenarioActivationResponse,
  type ScenarioAlert,
  type ScenarioChangeSummary,
  type ScenarioOperation,
  type ScenarioResponse,
} from '@/common/api/types/planner/scenario';

export type CreateScenarioRequest = z.infer<typeof createScenarioRequestSchema>;

export type UpdateScenarioRequest = z.infer<typeof scenarioUpdateRequestSchema>;

export type ScenarioActivationOutcome =
  | { accepted: true; operationId: number }
  | { accepted: false; activation: ScenarioActivationResponse };

export type ScenarioSyncOutcome = { accepted: true; operationId: number } | { accepted: false };

const ALB_IDLE_TIMEOUT_MS = 60_000;

const OPERATION_ACCEPTED = 202;

const createScenario = async ({ loopId, name }: { loopId: number } & CreateScenarioRequest) => {
  const response = await tempoApi()
    .post(`api/v1/adaptive-planner/loops/${loopId}/scenarios`, {
      json: { name },
    })
    .json();
  return parsedScenarioSchema.parse(response);
};

const getScenarios = async ({ loopId }: { loopId: number }) => {
  const response = await tempoApi().get(`api/v1/adaptive-planner/loops/${loopId}/scenarios`).json();
  return z.array(parsedScenarioResponseSchema).parse(response);
};

const getScenario = async ({
  loopId,
  scenarioId,
  includeMetrics = false,
}: {
  loopId: number;
  scenarioId: number;
  includeMetrics?: boolean;
}): Promise<ScenarioResponse> => {
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}`, {
      searchParams: { includeMetrics: String(includeMetrics) },
    })
    .json();
  return parsedScenarioResponseSchema.parse(response);
};

const updateScenario = async ({
  loopId,
  scenarioId,
  data,
}: {
  loopId: number;
  scenarioId: number;
  data: UpdateScenarioRequest;
}) => {
  const response = await tempoApi()
    .patch(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}`, {
      json: data,
    })
    .json();
  return parsedScenarioSchema.parse(response);
};

const getAlerts = async ({
  loopId,
  scenarioId,
  alertTypes,
}: {
  loopId: number;
  scenarioId: number;
  alertTypes?: ScenarioAlert['alertType'][];
}): Promise<ScenarioAlert[]> => {
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/alerts`, {
      searchParams: new URLSearchParams(alertTypes?.map((type) => ['alertTypes', type])),
    })
    .json();
  return z.array(scenarioAlertResponseSchema).parse(response);
};

const getChangeSummary = async ({
  loopId,
  scenarioId,
}: {
  loopId: number;
  scenarioId: number;
}): Promise<ScenarioChangeSummary> => {
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/change-summary`)
    .json();
  return scenarioChangeSummarySchema.parse(response);
};

const IMPACT_SUMMARY_SORT = {
  SEVERITY: 'SEVERITY',
  SLIP: 'SLIP',
};

export type ImpactSummarySort = (typeof IMPACT_SUMMARY_SORT)[keyof typeof IMPACT_SUMMARY_SORT];

const getImpactSummary = async ({
  loopId,
  scenarioId,
  maxItemChanges,
  sortBy = IMPACT_SUMMARY_SORT.SLIP,
}: {
  loopId: number;
  scenarioId: number;
  maxItemChanges?: number;
  sortBy?: ImpactSummarySort;
}) => {
  const searchParams = new URLSearchParams({ sortBy });
  if (maxItemChanges !== undefined) {
    searchParams.set('maxItemChanges', String(maxItemChanges));
  }

  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/impact-summary`, {
      searchParams,
    })
    .json();

  return scenarioImpactSummaryResponseSchema.parse(response);
};

export const IMPACT_LIST_DIRECTION = {
  BEFORE: 'BEFORE',
  AFTER: 'AFTER',
} as const;

export type ImpactListDirection =
  (typeof IMPACT_LIST_DIRECTION)[keyof typeof IMPACT_LIST_DIRECTION];

interface ImpactListParams {
  loopId: number;
  scenarioId: number;
  direction: ImpactListDirection;
  limit?: number;
  cursor?: string;
}

const impactListSearchParams = ({
  direction,
  limit,
  cursor,
}: Omit<ImpactListParams, 'loopId' | 'scenarioId'>) => {
  const searchParams = new URLSearchParams({ direction });
  if (limit !== undefined) {
    searchParams.set('limit', String(limit));
  }
  if (cursor) {
    searchParams.set('cursor', cursor);
  }
  return searchParams;
};

const impactListUrl = (loopId: number, scenarioId: number, path: string) =>
  `api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/impact-summary/${path}`;

const getImpactAtRisk = async ({ loopId, scenarioId, ...rest }: ImpactListParams) => {
  const response = await tempoApi()
    .get(impactListUrl(loopId, scenarioId, 'at-risk'), {
      searchParams: impactListSearchParams(rest),
    })
    .json();
  return paginatedResultRiskItemEntrySchema.parse(response);
};

const getImpactUnestimatedItems = async ({ loopId, scenarioId, ...rest }: ImpactListParams) => {
  const response = await tempoApi()
    .get(impactListUrl(loopId, scenarioId, 'unestimated-items'), {
      searchParams: impactListSearchParams(rest),
    })
    .json();
  return paginatedResultItemRefSchema.parse(response);
};

const getImpactUnassignedItems = async ({ loopId, scenarioId, ...rest }: ImpactListParams) => {
  const response = await tempoApi()
    .get(impactListUrl(loopId, scenarioId, 'unassigned-items'), {
      searchParams: impactListSearchParams(rest),
    })
    .json();
  return paginatedResultItemRefSchema.parse(response);
};

const getImpactTeamsWithoutCapacity = async ({ loopId, scenarioId, ...rest }: ImpactListParams) => {
  const response = await tempoApi()
    .get(impactListUrl(loopId, scenarioId, 'teams-without-capacity'), {
      searchParams: impactListSearchParams(rest),
    })
    .json();
  return paginatedResultTeamRefSchema.parse(response);
};

const loopSync = async ({
  loopId,
  scenarioId,
}: {
  loopId: number;
  scenarioId: number;
}): Promise<ScenarioSyncOutcome> => {
  const response = await tempoApi().post(
    `api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/loop-sync`,
    { timeout: ALB_IDLE_TIMEOUT_MS },
  );

  if (response.status !== OPERATION_ACCEPTED) {
    return { accepted: false };
  }

  const body = await response.json();
  return {
    accepted: true,
    operationId: scenarioOperationAcceptedResponseSchema.parse(body).operationId,
  };
};

const deleteScenario = async ({ loopId, scenarioId }: { loopId: number; scenarioId: number }) => {
  await tempoApi().delete(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}`);
};

const resetScenario = async ({
  loopId,
  scenarioId,
}: {
  loopId: number;
  scenarioId: number;
}): Promise<void> => {
  await tempoApi().post(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/reset`, {
    timeout: ALB_IDLE_TIMEOUT_MS,
  });
};

const schedule = async ({
  loopId,
  scenarioId,
}: {
  loopId: number;
  scenarioId: number;
}): Promise<void> => {
  await tempoApi().post(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/schedule`);
};

const activate = async ({
  loopId,
  scenarioId,
  pushToProvider,
}: {
  loopId: number;
  scenarioId: number;
  pushToProvider: boolean;
}): Promise<ScenarioActivationOutcome> => {
  const response = await tempoApi().post(
    `api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/activate`,
    {
      searchParams: { pushToProvider: String(pushToProvider) },
      timeout: ALB_IDLE_TIMEOUT_MS,
    },
  );
  const body = await response.json();

  // The status, not the feature flag, decides which shape came back: adaptive-planner evaluates
  // the flag per-org, and frontend and backend deploy independently, so a client with the flag on
  // can still be talking to a build that runs the activation inline.
  if (response.status === OPERATION_ACCEPTED) {
    return {
      accepted: true,
      operationId: scenarioOperationAcceptedResponseSchema.parse(body).operationId,
    };
  }
  return { accepted: false, activation: scenarioActivationResponseSchema.parse(body) };
};

const getOperation = async ({
  loopId,
  scenarioId,
  operationId,
}: {
  loopId: number;
  scenarioId: number;
  operationId: number;
}): Promise<ScenarioOperation> => {
  const response = await tempoApi()
    .get(
      `api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/operations/${operationId}`,
    )
    .json();
  return scenarioOperationResponseSchema.parse(response);
};

export const LoopPlannerScenarioApi = {
  createScenario,
  getScenarios,
  getScenario,
  updateScenario,
  getAlerts,
  getChangeSummary,
  getImpactSummary,
  getImpactAtRisk,
  getImpactUnestimatedItems,
  getImpactUnassignedItems,
  getImpactTeamsWithoutCapacity,
  loopSync,
  deleteScenario,
  resetScenario,
  schedule,
  activate,
  getOperation,
} as const;
